import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import type { AdminPurchase, UserProfileEvent } from "@/services/api/management";
import { eligibleTargets } from "./purchaseEligibility";

export type PurchaseAction = "transfer" | "assign";

interface PurchaseActionDialogProps {
  purchase: AdminPurchase | null;
  action: PurchaseAction;
  events: UserProfileEvent[];
  busy: boolean;
  refusal: string | null;
  onSubmit: (targetUuid: string, reason: string) => void;
  onClose: () => void;
}

const MIN_REASON = 5;

/**
 * JUB-157: transfer (or assign) one purchase. Target, reason and an explicit
 * irreversible confirmation are all required before the request is sent.
 *
 * The form belongs to one (purchase, action): the parent keys the dialog by
 * it, and the state is also reset here when that identity changes, so a
 * checked confirmation or a chosen target never carries over to the next
 * purchase. The target must still be eligible in the current events list.
 */
export function PurchaseActionDialog({ purchase, action, events, busy, refusal, onSubmit, onClose }: PurchaseActionDialogProps) {
  const { t } = useTranslation();
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const identity = purchase ? `${purchase.id}:${action}` : null;
  useEffect(() => {
    setTarget("");
    setReason("");
    setConfirmed(false);
  }, [identity]);

  if (!purchase) return null;

  const targets = eligibleTargets(purchase, events);
  // A target that left the eligible list (e.g. after a refresh) is dropped.
  const selectedTarget = targets.some((event) => event.event_uuid === target) ? target : "";
  const canSubmit = selectedTarget !== "" && reason.trim().length >= MIN_REASON && confirmed && !busy;
  const close = () => {
    setTarget("");
    setReason("");
    setConfirmed(false);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent data-testid="admin-purchase-action-dialog">
        <DialogHeader>
          <DialogTitle>{t(`management.purchases.${action}Title`)}</DialogTitle>
          <DialogDescription>
            {t(`management.purchases.${action}Description`, { plan: purchase.plan, event: purchase.eventName ?? "—" })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="admin-purchase-target">{t("management.purchases.target")}</Label>
            {targets.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="admin-purchase-no-targets">
                {t("management.purchases.noEligibleTargets")}
              </p>
            ) : (
              <select
                id="admin-purchase-target"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedTarget}
                onChange={(e) => setTarget(e.target.value)}
                data-testid="admin-purchase-target"
              >
                <option value="">{t("management.purchases.chooseTarget")}</option>
                {targets.map((event) => (
                  <option key={event.event_uuid} value={event.event_uuid}>
                    {event.event_name} · {event.plan}
                    {event.archived ? ` · ${t("management.purchases.archived")}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="admin-purchase-reason">{t("management.purchases.reason")}</Label>
            <Textarea
              id="admin-purchase-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("management.purchases.reasonPlaceholder")}
              data-testid="admin-purchase-reason"
            />
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="admin-purchase-confirm"
              checked={confirmed}
              onCheckedChange={(value) => setConfirmed(value === true)}
              data-testid="admin-purchase-confirm"
            />
            <Label htmlFor="admin-purchase-confirm" className="text-sm font-normal leading-snug">
              {t("management.purchases.irreversible")}
            </Label>
          </div>

          {refusal && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="admin-purchase-refusal">
              {t(`management.purchases.refusals.${refusal}`) || refusal}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => canSubmit && onSubmit(selectedTarget, reason.trim())} disabled={!canSubmit} data-testid="admin-purchase-submit">
            {t(`management.purchases.${action}Submit`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
